import { useEffect, useState } from "react";

import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { PatientPortalClient } from "@/Common/Api/Patient/PatientPortalClient";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Person } from "@/Common/Models/Person/Person";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { GetPatientOverviewResponse } from "@/Common/Models/Portal/MedfarPortal";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemActionType } from "@/Common/Models/Portal/SharedItem/SharedItemActionType";
import { SharedItemMessagePatientResponseType } from "@/Common/Models/Portal/SharedItem/SharedItemMessagePatientResponseType";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { LinkButton } from "@/DesignSystem";
import { HyperLinkRedirectControl } from "../Buttons/HyperLinkRedirectControl";
import { ClickableTextPanel } from "../ClickableText/ClickableTextPanel";
import { FixedClickableTextPanel } from "../ClickableText/FixedClickableTextPanel";
import { FeatureFlagControl } from "../FeatureFlagControl";
import { CheckboxControl } from "../Inputs/CheckboxControl";
import { TextboxControl } from "../Inputs/TextboxControl";
import { PersonSearchAutocompleteCustom } from "../Search/PersonSearchAutocompleteCustom";
import { IPortalCommunicationMessageFormState } from "./Hooks/usePortalCommunicationMessageForm";

import styles from "./PortalCommunicationMessageForm.module.scss";

interface IPortalCommunicationMessageFormProps {
    componentId: string;
    subject?: string;
    warningMessage?: string;
    containerClass?: string;
    formContainerClass?: string;
    sharedItem?: SharedItem;
    currentPatientPortalOverview?: GetPatientOverviewResponse;
    currentPatient?: Person;
    isPopup?: boolean;
    showHeader?: boolean
    showPatientCanReplyWithAttachment?: boolean
    hideToField?: boolean;
    hideSubjectField?: boolean;
    actionType: SharedItemActionType;
    onNewPortalCommunicationMessageChange: (newPortalCommunicationMessage: Partial<IPortalCommunicationMessageFormState>) => void;
    isMessageClickableTextPanelOpen?: boolean;
    onMessageClickableTextPanelOpen?: () => void;
    onClickableTextPanelClose?: () => void;
}

function PortalCommunicationMessageForm(props: Readonly<IPortalCommunicationMessageFormProps>): JSX.Element {
    const { getText } = useTranslation();

    const [messageText, setMessageText] = useState<string>("");
    const [subject, setSubject] = useState<string>(props.subject);
    const [priority, setPriority] = useState<SharedItemPriority>(SharedItemPriority.Normal);
    const [canPatientReplyWithMessage, setCanPatientReplyWithMessage] = useState<boolean>(props.sharedItem?.CanPatientReplyWithMessage ?? false);
    const [patient, setPatient] = useState<Person>(null);
    const [showMessageClickableText, setShowMessageClickableText] = useState<boolean>(false);
    const [showSubjectClickableText, setShowSubjectClickableText] = useState<boolean>(false);
    const [portalAccountOverview, setPortalAccountOverview] = useState<GetPatientOverviewResponse>(props.currentPatientPortalOverview);

    const initialResponseTypes: SharedItemMessagePatientResponseType[] = [];
    if (canPatientReplyWithMessage) {
        initialResponseTypes.push(SharedItemMessagePatientResponseType.Message);
    }
    const [patientResponseTypes, setPatientResponseTypes] = useState<SharedItemMessagePatientResponseType[]>(initialResponseTypes);

    const isTextValid = !IsNullOrWhiteSpace(subject) || !IsNullOrWhiteSpace(messageText);
    const maxSubjectLengthLimit = 500;

    const representativeTypeLabel = (representativeType: string): string => {
        if (!representativeType) {
            return "";
        }

        return cacheStore.GetRAMQTypeRepresentant(true).filter(p => p[0] === representativeType).map(p => p[1]).toString();
    };

    const renderPatientSelect = (): JSX.Element => {
        const selectedPatient = patient ? [patient] : [];

        return (
            <div data-cy="portal-communications-patient-autocomplete" className={styles.listAutocomplete}>
                <PersonSearchAutocompleteCustom
                    componentId="patientPortalCommunicationAutocomplete"
                    focusOnLoad={false}
                    disabled={false}
                    valid={!!selectedPatient.length}
                    selectedItemList={selectedPatient.toImmutable()}
                    onSelectionCallback={(personItem: PersonDropdownItem) => handlePatientChange(personItem.Item)}
                    onClearCallback={() => handlePatientChange(null)}
                    closeDropDownAfterSelection={true}
                    allowMultipleItemSelection={false}
                />
            </div>
        );
    };

    const renderPatientDisplay = (): JSX.Element => {
        const representatives = props.currentPatient.Relationships?.filter(p => p.IsRepresentative) ?? [];

        return (
            <>
                <div className="margin-xs-top" data-cy="portal-communications-patient-name-container">
                    <HyperLinkRedirectControl
                        target={RedirectTo.Patient}
                        redirectValue={props.currentPatient.ContactId}
                        text={props.currentPatient.NameWithPrefix}
                    />
                </div>
                {representatives.length > 0 && (
                    <div className={styles.representativesContainer}>
                        <label data-cy="portal-communications-representatives-label">{getText("RepresentativeLabel")}</label>
                        <div className={styles.representatives}>
                            {representatives.map((linkedContact, index) => (
                                <div key={`${index} ${linkedContact.Id}`} data-cy="portal-communications-representatives-container">
                                    <span>{LinkedContact.GetFullNameWithPrefix(linkedContact)}</span>
                                    <span className="margin-xs-left">{`(${representativeTypeLabel(linkedContact.RepresentativeType)})`}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </>
        );
    };

    const portalWarningLabel = (): string => {
        const isChildAccount = PortalHelper.hasGrantedAccessToOtherProfiles(portalAccountOverview) && (
            props.currentPatient?.PortalSyncState === PortalRegistrationState.ChildAccount ||
            props.currentPatient?.PortalSyncState === PortalRegistrationState.RegistrationPending
        );

        return getText(isChildAccount ? "ChildAccountWarningLabel" : "InactiveAccountWarningLabel");
    };

    const closeClickableTextPanel = (): void => {
        setShowMessageClickableText(false);
        setShowSubjectClickableText(false);
        props.onClickableTextPanelClose?.();
    };

    const openClickableTextPanel = (): void => {
        setShowMessageClickableText(true);
        props.onMessageClickableTextPanelOpen?.();
    };

    const handlePatientResponseTypeChange = (isChecked: boolean, responseType: SharedItemMessagePatientResponseType): void => {
        let updatedResponseTypes = [];
        if (isChecked) {
            updatedResponseTypes = [...patientResponseTypes, responseType];
        }
        else {
            updatedResponseTypes = patientResponseTypes.filter(selected => selected !== responseType);
        }

        if (responseType === SharedItemMessagePatientResponseType.Message) {
            setCanPatientReplyWithMessage(isChecked);
            props.onNewPortalCommunicationMessageChange({ canPatientReplyWithMessage: isChecked });
        }

        setPatientResponseTypes(updatedResponseTypes);
    };

    const handlePriorityChange = (selectedPriority: SharedItemPriority): void => {
        setPriority(selectedPriority);
        props.onNewPortalCommunicationMessageChange({ priority: selectedPriority });
    };

    const handlePatientChange = (selectedPatient: Person): void => {
        setPatient(selectedPatient);
        props.onNewPortalCommunicationMessageChange({ patient: selectedPatient });
    };

    useEffect(() => {
        const loadPortalAccountOverview = async (): Promise<void> => {
            const patientContactId = patient?.ContactId;
            if (patientContactId) {
                const portalPatientOverview = await PatientPortalClient.loadPortalAccountOverviewAsync(patientContactId);
                setPortalAccountOverview(portalPatientOverview);
            }
        };

        loadPortalAccountOverview();
    }, [patient?.ContactId]);

    useEffect(() => {
        if (!IsNullOrUndefined(props.isMessageClickableTextPanelOpen))
            setShowMessageClickableText(props.isMessageClickableTextPanelOpen);
    }, [props.isMessageClickableTextPanelOpen]);

    return (
        <div onClick={() => closeClickableTextPanel()} id={props.componentId} className={`${styles.replyForwardFormContainer} ${props.containerClass}`}>
            {props.showHeader && (
                <div className={styles.replyHeader}>
                    <span className={styles.label}>{getText("FromLabel")}</span>
                    <span>
                        {sessionStore.getCurrentEmployee().nameWithPrefix()}
                    </span>
                </div>
            )}
            <div className={`${props.isPopup ? styles.popupForm : ""} ${styles.messageForm} ${props.formContainerClass}`}>
                {!props.hideToField && (
                    <div className={`${styles.row} ${props.isPopup ? styles.popupAlignment : ""}`} data-cy="portal-communications-to-field">
                        <div className={styles.labelContainer}>
                            <label className={styles.label}>{getText("ToM2Label")}</label>
                        </div>
                        <div className={styles.formElementContainer}>
                            {props.currentPatient
                                ? renderPatientDisplay()
                                : renderPatientSelect()
                            }
                        </div>
                    </div>
                )}

                {(!!props.warningMessage || (!!props.currentPatient && props.currentPatient.PortalSyncState !== PortalRegistrationState.RegistrationComplete)) && (
                    <div className={`${styles.row} ${props.isPopup ? styles.popupAlignment : ""}`}>
                        <div className={styles.labelContainer} />
                        <div className={`${styles.warningLabel} ${styles.formElementContainer}`} data-cy="portal-communications-warning-message">
                            {props.warningMessage ?? portalWarningLabel()}
                        </div>
                    </div>
                )}

                <div className={`${styles.row} ${props.isPopup ? styles.popupAlignment : ""}`}>
                    <div className={styles.labelContainer}>
                        <label className={styles.label}>{getText("PriorityLabel")}</label>
                    </div>
                    <div className={`${styles.formElementContainer} ${styles.priorityContainer}`}>
                        <div className={styles.prioritySelect}>
                            <select
                                onChange={e => handlePriorityChange(parseInt(e.target.value))}
                                className="form-control"
                                value={priority}
                                data-cy="portal-communications-selected-priority"
                            >
                                {EnumHelper.getNamesAndValues(SharedItemPriority).map((priority, index) => (
                                    <option
                                        key={`${index}_${priority.name}`}
                                        value={priority.value}
                                    >
                                        {getText(`Enums_SharedItemPriority_${priority.name}`)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.priorityHighLink}>
                            {priority !== SharedItemPriority.High && (
                                <a onClick={() => handlePriorityChange(SharedItemPriority.High)} data-cy="portal-communications-high-priority-click">
                                    {getText("Enums_SharedItemPriority_High")}
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {(props.actionType === SharedItemActionType.New) && !props.hideSubjectField && (
                    <div className={`${styles.row} ${props.isPopup ? styles.popupAlignment : ""}`} data-cy="portal-communications-subject-container">
                        <div className={styles.labelContainer}>
                            <label className={styles.label}>{getText("SubjectLabel")}</label>
                        </div>
                        <div className={styles.textContainer}>
                            <TextboxControl
                                id="portalCommunicationSubject"
                                debounceMs={200}
                                onChange={(value: string) => {
                                    if (value.length > maxSubjectLengthLimit) {
                                        sessionStore.showPopup({
                                            title: getText("ErrorMLabel"),
                                            content: getText("MaxCharacterLimitErrorLabel")
                                        });
                                        return;
                                    }
                                    setSubject(value);
                                    props.onNewPortalCommunicationMessageChange({ subject: value });
                                }}
                                className="form-control text-left"
                                value={subject ?? ""}
                                valid={isTextValid || !!props.isPopup}
                                maxLength={SharedItem.SubjectMaxLength}
                                dataCy="portal-communications-subject"
                            />
                            <FeatureFlagControl
                                flag={props.isPopup}
                                activeFeature={(
                                    <FixedClickableTextPanel
                                        cssClass="text-helper-container"
                                        context="PortalSubject"
                                        inputElementId="portalCommunicationSubject"
                                        isClickableTextPanelOpen={showSubjectClickableText}
                                        onClickableTextPanelOpen={() => setShowSubjectClickableText(true)}
                                        onClickableTextPanelClose={() => setShowSubjectClickableText(false)}
                                        doNotAdjustPosition={!!props.isPopup}
                                        reduceHeight={!!props.isPopup}
                                        isLinkDisabled={false}
                                        isAboveLabel
                                    />
                                )}
                                fallbackFeature={(
                                    <>
                                        {showSubjectClickableText && (
                                            <div className="clickable-text-container">
                                                <ClickableTextPanel
                                                    leftAlign={true}
                                                    cssClass="text-helper-container"
                                                    context="PortalSubject"
                                                    inputElementId="portalCommunicationSubject"
                                                    onCloseEvent={() => setShowSubjectClickableText(false)}
                                                />
                                            </div>
                                        )}
                                        <div className="text-right">
                                            <LinkButton
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setShowSubjectClickableText(true);
                                                }}
                                                label={getText("SelectFromListLabel")}
                                            />
                                        </div>
                                    </>
                                )
                                }
                            />
                        </div>
                    </div>
                )}
                <div className={`${styles.row} ${props.isPopup ? styles.popupAlignment : ""}`}>
                    <div className={styles.labelContainer}>
                        <label className={styles.label}>{getText("MessageLabel")}</label>
                    </div>
                    <div className={styles.textContainer}>
                        <textarea
                            id="portalCommunicationMessage"
                            className={`form-control ${(!isTextValid && !props.isPopup) ? "invalid" : ""} ${styles.messageBox}`}
                            style={props.isPopup ? { resize: "none" } : { maxHeight: "205px" }}
                            value={messageText}
                            onChange={e => {
                                setMessageText(e.target.value);
                                props.onNewPortalCommunicationMessageChange({ messageText: e.target.value });
                            }}
                            rows={3}
                            data-cy="portal-communications-message"
                            autoFocus
                        />
                        <FeatureFlagControl
                            flag={props.isPopup}
                            activeFeature={(
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="PORTAL"
                                    inputElementId="portalCommunicationMessage"
                                    isClickableTextPanelOpen={showMessageClickableText}
                                    onClickableTextPanelOpen={() => openClickableTextPanel()}
                                    onClickableTextPanelClose={() => closeClickableTextPanel()}
                                    doNotAdjustPosition={!!props.isPopup}
                                    reduceHeight={!!props.isPopup}
                                    isLinkDisabled={false}
                                    isAboveLabel
                                />
                            )}
                            fallbackFeature={(
                                <>
                                    {showMessageClickableText && (
                                        <div className="clickable-text-container">
                                            <ClickableTextPanel
                                                leftAlign={true}
                                                cssClass="text-helper-container"
                                                context="PORTAL"
                                                inputElementId="portalCommunicationMessage"
                                                onCloseEvent={() => closeClickableTextPanel()}
                                            />
                                        </div>
                                    )}
                                    <div className="text-right">
                                        <LinkButton
                                            onClick={e => {
                                                e.stopPropagation();
                                                openClickableTextPanel();
                                            }}
                                            label={getText("SelectFromListLabel")}
                                        />
                                    </div>
                                </>
                            )
                            }
                        />
                    </div>
                </div>

                <div className={`${styles.row} ${props.isPopup ? styles.popupAlignment : ""}`}>
                    <div className={styles.labelContainer}>
                        <label className={styles.label}>{getText("SharedItem_PatientResponseLabel")}</label>
                    </div>
                    <div className={styles.patientResponse}>
                        <CheckboxControl
                            id="ResponseTypeMessage_checkbox"
                            checked={patientResponseTypes.includes(SharedItemMessagePatientResponseType.Message)}
                            label={getText("MessageLabel")}
                            labelStyle={{ margin: "0" }}
                            onChange={(checked: boolean) => handlePatientResponseTypeChange(checked, SharedItemMessagePatientResponseType.Message)}
                            dataCy="portal-communications-patient-response-message"
                            DivWrap
                        />
                        {props.showPatientCanReplyWithAttachment && (
                            <CheckboxControl
                                id="ResponseTypeAttachment_checkbox"
                                checked={patientResponseTypes.includes(SharedItemMessagePatientResponseType.Attachment)}
                                label={getText("AttachmentLabel")}
                                labelStyle={{ margin: "0" }}
                                onChange={(checked: boolean) => handlePatientResponseTypeChange(checked, SharedItemMessagePatientResponseType.Attachment)}
                                DivWrap
                                dataCy="portal-communications-patient-response-attachment"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export { PortalCommunicationMessageForm };
